/**
 * Prazo para as promessas do boot
 *
 * ## Por que existe
 *
 * O boot do app espera a rede em dois pontos, e **nenhum dos dois tem prazo**:
 *
 *  1. `APP_INITIALIZER` → `RestaurantService.loadByURL()` (`core.module.ts`).
 *     O Angular só faz o bootstrap depois que TODO initializer resolve. Enquanto
 *     isso a tela mostra o esqueleto estático do `index.html`, com o texto
 *     "Carregando o cardápio…". Se a requisição do cardápio pendurar — nem
 *     resolve, nem rejeita — esse texto fica na tela **para sempre**.
 *  2. `AppComponent.ngOnInit` → `await getCachedOrderByUUID_forTracking()`.
 *     Aqui o app já subiu, mas a navegação de entrada mora DEPOIS do `await`.
 *     Pendurar aqui deixa o usuário numa tela vazia, também para sempre.
 *
 * Não há interceptor de timeout global no app: o único `timeout()` do código é o
 * da própria telemetria. Um `catch` não cobre isto — uma promessa que nunca
 * assenta não rejeita, ela simplesmente não volta.
 *
 * Isso importa porque o modo de falha é o pior possível: sem erro, sem log, sem
 * telemetria, e o cliente olhando uma tela parada. Foi o sintoma do MP-569
 * ("página fica apenas carregando", iPhone).
 *
 * ## O contrato
 *
 * `comPrazo` **sempre assenta e nunca rejeita**. Ela devolve um resultado que
 * diz o que aconteceu, em vez de lançar — quem chama está num caminho onde
 * lançar significaria trocar uma tela parada por uma tela quebrada.
 *
 * O prazo não cancela a promessa original (não dá — Promise não é cancelável).
 * Ela continua correndo e, se voltar depois, o resultado é ignorado. O que o
 * prazo garante é que **quem espera volta a andar**.
 */

/** O que aconteceu com a promessa dentro do prazo. */
export interface ResultadoComPrazo<T> {
  /** O prazo estourou antes de a promessa assentar. */
  estourou: boolean;
  /** Valor resolvido. Só existe quando `estourou` é `false` e não houve erro. */
  valor?: T;
  /** Motivo da rejeição, quando a promessa rejeitou dentro do prazo. */
  erro?: any;
}

/** Injeção só para teste — em produção são os timers do browser. */
export interface TimersComPrazo {
  agendar?: (fn: () => void, ms: number) => any;
  cancelar?: (id: any) => void;
}

/**
 * Espera `promessa` por no máximo `prazoMs`.
 *
 * Nunca lança e nunca fica pendurada. Se `prazoMs` não for um número finito e
 * positivo, espera sem prazo — é a degradação segura para uma configuração
 * errada, porque um prazo de `0` ou `NaN` cortaria toda requisição na largada.
 */
export function comPrazo<T>(
  promessa: Promise<T> | any,
  prazoMs: number,
  timers: TimersComPrazo = {},
): Promise<ResultadoComPrazo<T>> {
  const agendar = timers.agendar || ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const cancelar = timers.cancelar || ((id: any) => clearTimeout(id));

  return new Promise<ResultadoComPrazo<T>>((resolver) => {
    let respondido = false;
    let timer: any = null;

    // Um único ponto de saída: garante que `resolver` roda no máximo uma vez,
    // não importa quem chegue primeiro (a promessa ou o prazo).
    const responder = (r: ResultadoComPrazo<T>) => {
      if (respondido) return;
      respondido = true;
      if (timer !== null) {
        try { cancelar(timer); } catch (e) { /* timer exótico: nada a fazer */ }
      }
      resolver(r);
    };

    const prazoValido = typeof prazoMs === 'number' && isFinite(prazoMs) && prazoMs > 0;
    if (prazoValido) {
      try {
        timer = agendar(() => responder({ estourou: true }), prazoMs);
      } catch (e) {
        timer = null; // sem timers: vira espera sem prazo, o comportamento de hoje
      }
    }

    // `Promise.resolve` normaliza thenable, valor cru e `undefined` — quem chama
    // não precisa garantir que o que passou é mesmo uma Promise.
    try {
      Promise.resolve(promessa).then(
        (valor: T) => responder({ estourou: false, valor }),
        (erro: any) => responder({ estourou: false, erro }),
      );
    } catch (e) {
      responder({ estourou: false, erro: e });
    }
  });
}
